import { Button } from "react-bootstrap";
import {
  DragItem,
  ItemTypes,
  SortableTreatmentRowProps,
} from "../../../../models/state/IStoreTreatment";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { observer } from "mobx-react";
import { FormattedMessage } from "react-intl";
import { formatNumberWithCommas } from "../../../../constants/constants";

// Sortable treatment row component
export const SortableTreatmentRow: React.FC<SortableTreatmentRowProps> = ({
  id,
  treatment,
  index,
  categoryId,
  moveItem,
  onDetailClick,
}) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TREATMENT,
    item: (): DragItem => {
      return {
        type: ItemTypes.TREATMENT,
        id,
        index,
        parentId: categoryId,
        treatmentId: treatment.Id,
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  interface DropResult {
    handlerId: string | symbol | null;
  }

  const [{ handlerId, isOver }, drop] = useDrop<DragItem, void, { handlerId: string | symbol | null; isOver: boolean }>({
    accept: ItemTypes.TREATMENT,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
      };
    },
    // Use canDrop to show visual feedback
    canDrop(item: DragItem, monitor) {
      const sourceCategoryId = item.parentId as string;
      const targetCategoryId = categoryId;
      // Only allow dropping within the same category
      return sourceCategoryId === targetCategoryId;
    },
    // Only execute on drop, not on hover
    drop(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      const sourceCategoryId = item.parentId as string;
      const targetCategoryId = categoryId;
      const hoverTreatmentId = Number(treatment.Id);
      const dragTreatmentId = Number(item.treatmentId);

      // Don't replace items with themselves
      if (dragIndex === hoverIndex && sourceCategoryId === targetCategoryId) {
        return;
      }

      // Reject drop if trying to move between categories
      if (sourceCategoryId !== targetCategoryId) {
        return;
      }

      // Time to actually perform the action - THIS WILL TRIGGER API CALL
      moveItem(
        dragIndex,
        hoverIndex,
        sourceCategoryId,
        targetCategoryId,
        dragTreatmentId,
        hoverTreatmentId
      );
    },
  });

  drag(drop(ref));

  const rowClassName = `border-top ${isDragging ? "dragging" : ""} ${isOver ? "drop-target" : ""}`;

  return (
    <tr ref={ref} className={rowClassName} data-handler-id={handlerId}>
      <td>
        <i className="fas fa-grip-vertical drag-handle">
          <svg
            id="fi_8379631"
            height="12"
            viewBox="0 0 24 24"
            width="12"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              id="drag-vertical"
              d="m10 19a2 2 0 1 1 -2-2 2 2 0 0 1 2 2zm-2-9a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm8 14a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2z"
              fill="rgb(0,0,0)"
            ></path>
          </svg>
        </i>
      </td>
      <td>{index + 1}</td>
      <td>{treatment.TreatmentName}</td>
      <td><i className="bi bi-currency-yen me-1"></i> {formatNumberWithCommas(treatment.FeeIncludingTax)}</td>
      <td>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => onDetailClick(treatment)}
        >
        <FormattedMessage id="details" />
        </Button>
      </td>
    </tr>
  );
};

export default observer(SortableTreatmentRow);
